// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import type { EnrollResponse, ErrorResponse } from "@yubikey-enrollment/shared-types";

export interface IBackendClient {
  enroll(csrPem: string, serialNumber: string, accessToken: string): Promise<EnrollResponse>;
}

export class UnauthorizedError extends Error {
  constructor() {
    super("Session expired. Please log in again.");
    this.name = "UnauthorizedError";
  }
}

export class BackendError extends Error {
  constructor(
    public readonly errorCode: string,
    message: string,
  ) {
    super(message);
    this.name = "BackendError";
  }
}

export class BackendClient implements IBackendClient {
  private readonly baseUrl: string;

  constructor(baseUrl?: string) {
    const url = baseUrl ??
      ((typeof process !== "undefined" && process.env?.VITE_API_URL) || "");
    // Strip trailing slash to avoid double-slash in URLs
    this.baseUrl = url.replace(/\/+$/, "");
  }

  async enroll(csrPem: string, serialNumber: string, accessToken: string): Promise<EnrollResponse> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/enroll`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ csr_pem: csrPem, serial_number: serialNumber }),
      });
    } catch {
      throw new Error("Unable to reach the enrollment backend. Please check your connection and try again.");
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    if (!response.ok) {
      let errorBody: ErrorResponse | undefined;
      try {
        errorBody = (await response.json()) as ErrorResponse;
      } catch {
        // response body wasn't JSON
      }
      if (errorBody?.error_code) {
        throw new BackendError(errorBody.error_code, errorBody.message);
      }
      throw new Error(`Backend returned ${response.status}`);
    }

    return (await response.json()) as EnrollResponse;
  }
}
